import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type TouchEvent,
  type TransitionEvent
} from 'react';

const SWIPE_TRANSITION_MS = 260;
const SWIPE_DISTANCE_RATIO_THRESHOLD = 0.18;
const SWIPE_VELOCITY_THRESHOLD = 0.45;
const FETCH_TRIGGER_FROM_END = 2;

interface UsePreviewCarouselParams<T> {
  items: T[];
  hasNextPage: boolean;
  isFetching: boolean;
  fetchNextPage: () => void;
}

const getRealIndex = (renderIndex: number, itemCount: number) => {
  if (itemCount <= 0) return -1;
  if (itemCount === 1) return 0;
  if (renderIndex === 0) return itemCount - 1;
  if (renderIndex === itemCount + 1) return 0;
  return renderIndex - 1;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export const usePreviewCarousel = <T>({
  items,
  hasNextPage,
  isFetching,
  fetchNextPage
}: UsePreviewCarouselParams<T>) => {
  const itemCount = items.length;
  const loopedItems = useMemo(() => {
    if (itemCount <= 1) return items;
    return [items[itemCount - 1], ...items, items[0]];
  }, [items, itemCount]);

  const viewportRef = useRef<HTMLDivElement>(null);
  const [renderIndex, setRenderIndex] = useState(0);
  const [dragOffsetY, setDragOffsetY] = useState(0);
  const [isTouching, setIsTouching] = useState(false);
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);
  const isAnimatingRef = useRef(false);
  const ignoreTouchRef = useRef(false);
  const touchStartYRef = useRef(0);
  const touchStartAtRef = useRef(0);
  const viewportHeightRef = useRef(0);
  const prevItemCountRef = useRef(itemCount);

  const currentRealIndex = getRealIndex(renderIndex, itemCount);

  useEffect(() => {
    const prevItemCount = prevItemCountRef.current;
    prevItemCountRef.current = itemCount;

    if (itemCount <= 1) {
      setIsTransitionEnabled(false);
      setRenderIndex(0);
      setDragOffsetY(0);
      setIsTouching(false);
      isAnimatingRef.current = false;
      return;
    }

    if (prevItemCount <= 1) {
      setIsTransitionEnabled(false);
      setRenderIndex(1);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsTransitionEnabled(true);
        });
      });
      return;
    }

    setIsTransitionEnabled(false);
    setRenderIndex((prevRenderIndex) => {
      const prevRealIndex = getRealIndex(prevRenderIndex, prevItemCount);
      const nextRealIndex = clamp(prevRealIndex, 0, itemCount - 1);
      return nextRealIndex + 1;
    });
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsTransitionEnabled(true);
      });
    });
  }, [itemCount]);

  useEffect(() => {
    if (isFetching || !hasNextPage || itemCount === 0) return;
    if (renderIndex <= 0 || (itemCount > 1 && renderIndex > itemCount)) return;
    if (currentRealIndex >= itemCount - FETCH_TRIGGER_FROM_END) {
      fetchNextPage();
    }
  }, [currentRealIndex, fetchNextPage, hasNextPage, isFetching, itemCount, renderIndex]);

  const snapToRenderIndexWithoutTransition = useCallback((nextIndex: number) => {
    setIsTransitionEnabled(false);
    setRenderIndex(nextIndex);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setIsTransitionEnabled(true);
        isAnimatingRef.current = false;
      });
    });
  }, []);

  const handleTouchStart = useCallback(
    (e: TouchEvent<HTMLDivElement>) => {
      if (itemCount <= 1 || isAnimatingRef.current) return;

      const target = e.target as HTMLElement;
      ignoreTouchRef.current = !!target.closest(
        'button, a, input, textarea, select, [data-no-swipe="true"]'
      );
      if (ignoreTouchRef.current) return;

      const touch = e.touches[0];
      touchStartYRef.current = touch.clientY;
      touchStartAtRef.current = performance.now();
      viewportHeightRef.current = e.currentTarget.clientHeight;
      setIsTouching(true);
      setDragOffsetY(0);
    },
    [itemCount]
  );

  const handleTouchMove = useCallback((e: TouchEvent<HTMLDivElement>) => {
    if (!isTouching || ignoreTouchRef.current || itemCount <= 1) return;
    const touch = e.touches[0];
    const deltaY = touch.clientY - touchStartYRef.current;
    setDragOffsetY(deltaY);
  }, [isTouching, itemCount]);

  const handleTouchEnd = useCallback(() => {
    if (ignoreTouchRef.current || itemCount <= 1) {
      ignoreTouchRef.current = false;
      setIsTouching(false);
      setDragOffsetY(0);
      return;
    }

    if (!isTouching) return;

    const elapsedMs = Math.max(performance.now() - touchStartAtRef.current, 1);
    const distance = dragOffsetY;
    const absDistance = Math.abs(distance);
    const viewportHeight = Math.max(viewportHeightRef.current, 1);
    const velocity = absDistance / elapsedMs;

    const shouldMoveByDistance = absDistance > viewportHeight * SWIPE_DISTANCE_RATIO_THRESHOLD;
    const shouldMoveByVelocity = velocity > SWIPE_VELOCITY_THRESHOLD;
    const shouldMove = shouldMoveByDistance || shouldMoveByVelocity;
    const direction = distance < 0 ? 1 : -1;

    setIsTouching(false);
    setDragOffsetY(0);

    if (!shouldMove) {
      return;
    }

    isAnimatingRef.current = true;
    const maxRenderIndex = itemCount + 1;
    setRenderIndex((prev) => clamp(prev + direction, 0, maxRenderIndex));
  }, [dragOffsetY, isTouching, itemCount]);

  const handleTrackTransitionEnd = useCallback(
    (e: TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (itemCount <= 1) {
        isAnimatingRef.current = false;
        return;
      }

      if (renderIndex === 0) {
        snapToRenderIndexWithoutTransition(itemCount);
        return;
      }

      if (renderIndex === itemCount + 1) {
        snapToRenderIndexWithoutTransition(1);
        return;
      }

      isAnimatingRef.current = false;
    },
    [itemCount, renderIndex, snapToRenderIndexWithoutTransition]
  );

  const trackStyle = useMemo<CSSProperties>(() => {
    const translate = `calc(${-renderIndex * 100}% + ${dragOffsetY}px)`;
    return {
      transform: `translate3d(0, ${translate}, 0)`,
      transition:
        !isTouching && isTransitionEnabled ? `transform ${SWIPE_TRANSITION_MS}ms ease-out` : 'none',
      willChange: 'transform'
    };
  }, [dragOffsetY, isTouching, isTransitionEnabled, renderIndex]);

  return {
    viewportRef,
    loopedItems,
    currentRealIndex,
    trackStyle,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTrackTransitionEnd
  };
};
